import 'intersection-observer'; // Required for Safari on iOS 12.1 and below.
import { RefObject, useEffect } from 'react';

// Derived from: https://betterprogramming.pub/lazy-loading-images-with-intersection-observer-in-react-ad6135f1ca59
const listenerCallbacks = new WeakMap();
let globalObserver: IntersectionObserver | undefined;

function handleIntersections(entries: IntersectionObserverEntry[]): void {
  entries.forEach((entry) => {
    if (listenerCallbacks.has(entry.target)) {
      const callback = listenerCallbacks.get(entry.target);

      if (globalObserver && (entry.isIntersecting || entry.intersectionRatio > 0)) {
        globalObserver.unobserve(entry.target);
        listenerCallbacks.delete(entry.target);
        callback();
      }
    }
  });
}

function getIntersectionObserver() {
  if (globalObserver === undefined) {
    globalObserver = new IntersectionObserver(handleIntersections, {
      rootMargin: '0px 0px 256px 0px',
    });
  }
  return globalObserver;
}

export function useIntersectionObserver(ref: RefObject<Element> | undefined, callback = () => {}) {
  useEffect(() => {
    if (!ref?.current) {
      return undefined;
    }
    const target = ref.current;
    const observer = getIntersectionObserver();

    listenerCallbacks.set(target, callback);
    observer.observe(target);

    return () => {
      listenerCallbacks.delete(target);
      observer.unobserve(target);
    };
  }, [callback, ref]);
}
